/**
 * PDF Report Export — Creator Revenue Potential Report
 * Uses jsPDF (text-based, A4) for reliable multi-page output.
 * Design: clean white background, black text, #FFD400 gold accents.
 *
 * Sections:
 *   1. Creator Profile        — identity, stats, bio
 *   2. Audience Quality       — credibility score, PPP
 *   3. Full Demographics      — gender, age (overall + gender-split), geo, cities, languages, ethnicities
 *   4. Creator Signals        — own interests, brand affinities, hashtags, mentions
 *   5. Brand Collaborations   — all brands with type, post count, engagement metrics
 *   6. Top Product Matches    — top 5 products with scoring breakdown
 *   7. GMV Estimate & Next Steps
 */

import jsPDF from 'jspdf';
import { proxyImageUrl } from '../api';

// ── Local types (mirror the backend shapes we need) ──────────────────────────

interface FactorEntry { weight: number; raw_score: number; contribution: number; why: string }
interface FactorBreakdown {
  category: FactorEntry; tags: FactorEntry; age: FactorEntry; price: FactorEntry;
  margin: FactorEntry; complexity: FactorEntry; proof: FactorEntry;
  interest_affinity: FactorEntry; total_score: number;
}
interface TopProduct {
  rank: number; title: string; price_min: number; price_max: number;
  vertical: string; category: string; tags: string[]; score: number;
  factor_breakdown: FactorBreakdown;
  gmv_low?: number; gmv_expected?: number; gmv_high?: number;
  primary_image_url?: string | null;
}
interface GmvResult {
  gmv_low: number; gmv_expected: number; gmv_high: number;
  tier: number; confidence: number; aov_estimated: number;
  assumptions: Record<string, unknown>;
}
interface PlatformGmvBreakdown {
  platform: string; handle: string; followers: number; engagement_rate: number;
  gmv_low: number; gmv_expected: number; gmv_high: number;
}
interface MultiPlatformGmvResult {
  platform_breakdown: PlatformGmvBreakdown[];
  overlap_factor: number;
  total_gmv_low: number; total_gmv_expected: number; total_gmv_high: number;
  total_aov_estimated: number; tier: number; confidence: number;
}

interface CollaborationBrand {
  name: string;
  username?: string | null;
  domain?: string | null;
  logo_url?: string | null;
  category: string;
  collaboration_type: string;
  post_count: number;
  total_likes?: number | null;
  total_comments?: number | null;
  total_views?: number | null;
}

interface AnalysisFields {
  followers: number | null;
  engagement_rate: number | null;
  avg_views: number | null;
  avg_likes?: number | null;
  avg_comments?: number | null;
  following_count?: number | null;
  posts_count?: number | null;
  audience_age_min: number | null;
  audience_age_max: number | null;
  audience_gender_affinity: string;
  primary_category: string | null;
  secondary_categories?: string[] | null;
  geo_top_countries: Array<{ country: string; pct: number }> | null;
  provider: string;
  credibility_score?: number | null;
  paid_post_performance?: number | null;
  audience_languages?: Array<{ code: string; name?: string; weight: number }> | null;
  audience_cities?: Array<{ code?: string; name?: string; weight: number }> | null;
  audience_ethnicities?: Array<{ code: string; name?: string; weight: number }> | null;
  notes?: Record<string, unknown> | null;
}

interface SinglePlatformAnalyzeResult {
  creator: {
    platform: string; handle: string; profile_url: string;
    profile_image_url?: string | null;
  };
  analysis: AnalysisFields;
  match_run_id: number;
  top_products: TopProduct[];
  gmv: GmvResult;
  collaboration_brands?: CollaborationBrand[] | null;
  metrics?: ParsedCreatorMetrics | null;
}
interface MultiPlatformAnalyzeResult {
  platforms: Array<{
    platform: string; handle: string;
    creator: { platform: string; handle: string; profile_url: string };
    analysis: AnalysisFields;
  }>;
  match_run_id: number;
  top_products: TopProduct[];
  gmv: MultiPlatformGmvResult;
  is_multi: true;
}
type AnalyzeResult = SinglePlatformAnalyzeResult | MultiPlatformAnalyzeResult;

function isMultiResult(r: AnalyzeResult): r is MultiPlatformAnalyzeResult {
  return 'is_multi' in r && (r as MultiPlatformAnalyzeResult).is_multi === true;
}

interface ParsedCreatorMetrics {
  gender_distribution?: { male?: number; female?: number } | null;
  age_distribution_overall?: Array<{ label: string; followers_pct: number | null }> | null;
  age_distribution_male?: Array<{ label: string; followers_pct: number | null }> | null;
  age_distribution_female?: Array<{ label: string; followers_pct: number | null }> | null;
  geo_distribution?: Array<{ label: string; followers_pct: number | null }> | null;
  interests_ranked?: Array<{ label: string; followers_pct: number | null }> | null;
  brand_affinity_ranked?: Array<{ label: string; followers_pct: number | null }> | null;
}

// ── Formatters ───────────────────────────────────────────────────────────────

function fmtMoney(n: number): string {
  return '$' + n.toLocaleString('en-US', { maximumFractionDigits: 0 });
}

function sanitizeForPdf(text: string): string {
  return text
    .replace(/[\u2018\u2019\u201A]/g, "'")
    .replace(/[\u201C\u201D\u201E]/g, '"')
    .replace(/[\u2013\u2014]/g, '-')
    .replace(/[\u2026]/g, '...')
    .replace(/[\u00B7\u2022\u2023\u25E6]/g, '-')
    .replace(/[\u2192\u2190\u2191\u2193]/g, '->')
    .replace(/[^\x20-\x7E\n]/g, ' ')
    .replace(/ {2,}/g, ' ');
}
function fmtPct(n: number | null | undefined): string {
  return n == null ? '—' : (n * 100).toFixed(1) + '%';
}
function fmtNum(n: number | null | undefined): string {
  if (n == null) return '—';
  return n >= 1_000_000
    ? `${(n / 1_000_000).toFixed(1)}M`
    : n >= 1_000
    ? `${(n / 1_000).toFixed(0)}K`
    : String(n);
}
function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

// ── Tier copy ────────────────────────────────────────────────────────────────
const TIER_LABELS: Record<number, string> = {
  1: 'Tier 1 — Top Creator  ($250K+ GMV potential)',
  2: 'Tier 2 — Mid Creator  ($75K – $249K GMV potential)',
  3: 'Tier 3 — Entry Creator  (< $75K GMV potential)',
};
const TIER_ACTIONS: Record<number, string> = {
  1: 'Offer full 5-product catalog. Prioritise dedicated campaign planning and a dedicated onboarding call.',
  2: 'Start with 3-product starter catalog. Run a 30-day trial campaign with weekly check-ins.',
  3: 'Offer 1–2 hero products. Focus on building trust and engagement before expanding the catalog.',
};

// ── Color palette ────────────────────────────────────────────────────────────
const C = {
  black:    [11, 11, 11]    as [number, number, number],
  body:     [40, 40, 40]    as [number, number, number],
  muted:    [120, 120, 120] as [number, number, number],
  subtle:   [170, 170, 170] as [number, number, number],
  gold:     [255, 212, 0]   as [number, number, number],
  track:    [230, 230, 230] as [number, number, number],
  divider:  [220, 220, 220] as [number, number, number],
  cardBg:   [248, 248, 248] as [number, number, number],
  white:    [255, 255, 255] as [number, number, number],
  green:    [34, 197, 94]   as [number, number, number],
  yellow:   [234, 179, 8]   as [number, number, number],
  red:      [239, 68, 68]   as [number, number, number],
  blue:     [59, 130, 246]  as [number, number, number],
  purple:   [139, 92, 246]  as [number, number, number],
  teal:     [20, 184, 166]  as [number, number, number],
};

/**
 * Load an image URL as a JPEG data-URL suitable for jsPDF.addImage().
 *
 * Pipeline: proxy fetch → Blob → blob: URL → Image → Canvas → JPEG data-URL.
 *
 * Why this works when simpler approaches don't:
 *  - The server-side proxy bypasses CDN hotlink / Referer restrictions.
 *  - A blob: URL is same-origin so the canvas is never CORS-tainted.
 *  - Canvas export normalises any source format (WebP, AVIF, PNG…) to JPEG,
 *    which jsPDF reliably supports across all versions.
 *
 * Returns null on any failure so the PDF still renders without the image.
 */
async function loadImgBase64(url: string): Promise<string | null> {
  if (!url) return null;

  // Step 1 — Fetch raw image bytes via proxy (server-side, no CORS issues)
  let blob: Blob | null = null;
  const proxyUrl = proxyImageUrl(url);

  if (proxyUrl) {
    try {
      const r = await fetch(proxyUrl);
      if (r.ok) {
        const b = await r.blob();
        if (b.size > 0) blob = b;
      }
    } catch { /* continue */ }
  }

  // Fallback: try direct fetch with CORS mode (works for CDNs that return CORS headers)
  if (!blob) {
    try {
      const r = await fetch(url, { referrerPolicy: 'no-referrer', mode: 'cors' });
      if (r.ok) {
        const b = await r.blob();
        if (b.size > 0) blob = b;
      }
    } catch { /* CORS blocked or network error — continue */ }
  }

  if (!blob) return null;

  // Step 2 — Blob → same-origin blob URL → Image → Canvas → JPEG data URL
  const blobUrl = URL.createObjectURL(blob);
  try {
    return await new Promise<string | null>((resolve) => {
      const img = new Image();
      const timer = setTimeout(() => { resolve(null); }, 10_000);
      img.onload = () => {
        clearTimeout(timer);
        try {
          const canvas = document.createElement('canvas');
          canvas.width = img.naturalWidth || 200;
          canvas.height = img.naturalHeight || 200;
          const ctx = canvas.getContext('2d');
          if (!ctx) { resolve(null); return; }
          ctx.drawImage(img, 0, 0);
          resolve(canvas.toDataURL('image/jpeg', 0.92));
        } catch {
          resolve(null);
        }
      };
      img.onerror = () => { clearTimeout(timer); resolve(null); };
      img.src = blobUrl;
    });
  } finally {
    URL.revokeObjectURL(blobUrl);
  }
}

// ════════════════════════════════════════════════════════════════════════════
// Main export function
// ════════════════════════════════════════════════════════════════════════════

export async function exportMatchReport(
  result: AnalyzeResult,
  metrics: ParsedCreatorMetrics | null,
): Promise<void> {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const W = 210, H = 297;
  const margin = 18;
  const contentW = W - margin * 2;
  let y = margin;
  const multi = isMultiResult(result);

  const gmvLow       = multi ? result.gmv.total_gmv_low       : result.gmv.gmv_low;
  const gmvExpected  = multi ? result.gmv.total_gmv_expected   : result.gmv.gmv_expected;
  const gmvHigh      = multi ? result.gmv.total_gmv_high       : result.gmv.gmv_high;
  const gmvTierVal   = result.gmv.tier;
  const gmvConfidence= result.gmv.confidence;
  const gmvAov       = multi ? result.gmv.total_aov_estimated  : result.gmv.aov_estimated;

  // For single-platform, extract extra fields
  const singleResult = multi ? null : (result as SinglePlatformAnalyzeResult);
  const singleAnalysis = singleResult?.analysis ?? null;
  const singleNotes    = (singleAnalysis?.notes ?? {}) as Record<string, unknown>;
  const collabBrands: CollaborationBrand[] = (
    (singleResult?.collaboration_brands ?? null)
    ?? (singleNotes.collaboration_brands as CollaborationBrand[] | null)
    ?? []
  );

  // Merge metrics from result.metrics (Explore path) or passed-in metrics
  const mergedMetrics: ParsedCreatorMetrics = {
    ...(metrics ?? {}),
    ...((singleResult as { metrics?: ParsedCreatorMetrics })?.metrics ?? {}),
  };

  // ── Helper closures ───────────────────────────────────────────────────────

  function tc(...rgb: [number, number, number]) { doc.setTextColor(...rgb); }
  function fc(...rgb: [number, number, number]) { doc.setFillColor(...rgb); }
  function dc(...rgb: [number, number, number]) { doc.setDrawColor(...rgb); }

  function checkPage(needed = 20) {
    if (y + needed > H - 18) {
      doc.addPage();
      y = margin;
      pageFooter();
    }
  }

  function pageFooter() {
    const p = doc.getNumberOfPages();
    tc(...C.subtle);
    doc.setFontSize(7.5);
    doc.setFont('helvetica', 'normal');
    doc.text(
      `CreatorHub360 — Confidential — ${new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}  ·  Run #${result.match_run_id}  ·  Page ${p}`,
      margin, H - 10,
    );
    fc(...C.gold);
    doc.rect(margin, H - 14, contentW, 0.8, 'F');
  }

  function sectionHeader(title: string) {
    checkPage(18);
    y += 5;
    fc(...C.black);
    doc.rect(margin, y, 3, 7, 'F');
    fc(...C.gold);
    doc.rect(margin, y + 6, 3, 1.5, 'F');
    tc(...C.black);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text(title.toUpperCase(), margin + 6, y + 5.5);
    dc(...C.divider);
    doc.setLineWidth(0.3);
    doc.line(margin + 6, y + 8.5, margin + contentW, y + 8.5);
    y += 14;
    doc.setFont('helvetica', 'normal');
  }

  function metaRow(labelA: string, valA: string, labelB?: string, valB?: string) {
    checkPage(9);
    tc(...C.muted);
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.text(labelA.toUpperCase(), margin, y);
    if (labelB) doc.text(labelB.toUpperCase(), margin + contentW / 2, y);
    y += 3.5;
    tc(...C.black);
    doc.setFontSize(8.5);
    doc.setFont('helvetica', 'bold');
    doc.text(sanitizeForPdf(valA), margin, y);
    if (valB) doc.text(sanitizeForPdf(valB), margin + contentW / 2, y);
    doc.setFont('helvetica', 'normal');
    y += 6.5;
  }

  function barRow(
    label: string,
    pct: number,
    color: [number, number, number] = C.gold,
    labelWidth = 62,
  ) {
    checkPage(8);
    tc(...C.body);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    const labelText = sanitizeForPdf(label.slice(0, 36));
    doc.text(labelText, margin, y + 3.5);
    const barX = margin + labelWidth;
    const barW = contentW - labelWidth - 14;
    fc(...C.track);
    doc.rect(barX, y + 1, barW, 3.5, 'F');
    fc(...color);
    doc.rect(barX, y + 1, barW * Math.min(Math.max(pct, 0), 1), 3.5, 'F');
    tc(...C.muted);
    doc.setFontSize(7);
    doc.text(`${(pct * 100).toFixed(1)}%`, barX + barW + 2, y + 4);
    y += 8;
  }

  function subLabel(text: string, topGap = 2) {
    checkPage(8);
    y += topGap;
    tc(...C.muted);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    doc.text(text.toUpperCase(), margin, y);
    doc.setFont('helvetica', 'normal');
    y += 4;
  }

  function divider(gap = 4) {
    dc(...C.divider);
    doc.setLineWidth(0.2);
    doc.line(margin, y, margin + contentW, y);
    y += gap;
  }

  function pill(text: string, x: number, py: number, bgColor: [number, number, number], textColor: [number, number, number]) {
    const pw = doc.getTextWidth(text) + 4;
    fc(...bgColor);
    doc.roundedRect(x, py - 3, pw, 5, 1, 1, 'F');
    tc(...textColor);
    doc.setFontSize(6.5);
    doc.setFont('helvetica', 'bold');
    doc.text(text, x + 2, py + 0.5);
    doc.setFont('helvetica', 'normal');
    return pw + 2;
  }

  // ════════════════════════════════════════════════════════════════
  // PAGE 1 HEADER BAND
  // ════════════════════════════════════════════════════════════════

  fc(...C.black);
  doc.rect(0, 0, W, 40, 'F');
  fc(...C.gold);
  doc.rect(0, 38, W, 2, 'F');

  tc(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(19);
  doc.text('CreatorHub', margin, 20);
  const cwWidth = doc.getTextWidth('CreatorHub');
  tc(...C.gold);
  doc.text('360', margin + cwWidth, 20);

  tc(200, 200, 200);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9.5);
  doc.text('Creator Revenue Potential Report', margin, 30);

  tc(...C.subtle);
  doc.setFontSize(8);
  doc.text(
    new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }),
    W - margin, 30, { align: 'right' },
  );

  y = 52;

  // ════════════════════════════════════════════════════════════════
  // HERO — CREATOR IDENTITY (name, handle, photo)
  // ════════════════════════════════════════════════════════════════

  if (!multi) {
    const { creator, analysis } = singleResult!;
    const creatorFullName = singleNotes.full_name as string | null | undefined;

    // Load profile photo
    let heroImg: string | null = null;
    if (creator.profile_image_url) {
      try { heroImg = await loadImgBase64(creator.profile_image_url); } catch { /* skip */ }
    }

    const photoSize = 26;
    const textX = heroImg ? margin + photoSize + 6 : margin;

    checkPage(photoSize + 4);

    if (heroImg) {
      try { doc.addImage(heroImg, 'JPEG', margin, y, photoSize, photoSize, undefined, 'FAST'); } catch { /* skip */ }
    }

    // Full name in large bold font
    if (creatorFullName?.trim()) {
      tc(...C.black);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(18);
      doc.text(sanitizeForPdf(creatorFullName.trim()), textX, y + 9);
    }

    // Handle + platform + verified badge
    const handleLine = `@${creator.handle}`;
    tc(...C.muted);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    const handleY = creatorFullName?.trim() ? y + 17 : y + 9;
    doc.text(handleLine, textX, handleY);

    let badgeX = textX + doc.getTextWidth(handleLine) + 3;
    tc(...C.subtle);
    doc.setFontSize(9);
    doc.text(`·  ${capitalize(creator.platform)}`, badgeX, handleY);
    badgeX += doc.getTextWidth(`·  ${capitalize(creator.platform)}`) + 3;

    if (singleNotes.is_verified) {
      tc(...C.blue);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8);
      doc.text('[Verified]', badgeX, handleY);
      doc.setFont('helvetica', 'normal');
    }

    // Category beneath
    if (analysis.primary_category) {
      tc(...C.subtle);
      doc.setFontSize(8.5);
      const catY = creatorFullName?.trim() ? y + 23 : y + 15;
      doc.text(capitalize(analysis.primary_category), textX, catY);
    }

    y += Math.max(photoSize, creatorFullName?.trim() ? 26 : 18) + 4;
  }

  // ════════════════════════════════════════════════════════════════
  // SECTION 1 — CREATOR PROFILE (metrics)
  // ════════════════════════════════════════════════════════════════

  sectionHeader('Creator Profile');

  if (multi) {
    for (const p of result.platforms) {
      checkPage(26);
      tc(...C.gold);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8.5);
      doc.text(`${capitalize(p.platform)}  ·  @${p.handle}`, margin, y);
      doc.setFont('helvetica', 'normal');
      y += 5;
      metaRow('Followers', fmtNum(p.analysis.followers), 'Engagement Rate', fmtPct(p.analysis.engagement_rate));
      metaRow('Category', p.analysis.primary_category ?? '—', 'Gender', p.analysis.audience_gender_affinity);
    }
  } else {
    const { creator, analysis } = singleResult!;

    // Core metrics row 1
    metaRow('Followers', fmtNum(analysis.followers), 'Engagement Rate', fmtPct(analysis.engagement_rate));

    // Core metrics row 2
    const avgViewsLabel = creator.platform === 'youtube' ? 'Avg Views' : 'Avg Views / Plays';
    metaRow(avgViewsLabel, fmtNum(analysis.avg_views), 'Avg Likes', fmtNum(analysis.avg_likes));

    // Core metrics row 3
    metaRow(
      'Avg Comments', fmtNum(analysis.avg_comments),
      'Following Count', fmtNum(analysis.following_count),
    );

    // Core metrics row 4
    const ageRange = analysis.audience_age_min
      ? `${analysis.audience_age_min}–${analysis.audience_age_max}`
      : '—';
    metaRow(
      'Audience Age Range', ageRange,
      'Posts Count', fmtNum(analysis.posts_count),
    );

    metaRow(
      'Audience Gender', analysis.audience_gender_affinity,
      'Category', analysis.primary_category ?? '—',
    );

    // Secondary categories
    if (analysis.secondary_categories && analysis.secondary_categories.length > 0) {
      checkPage(8);
      tc(...C.muted);
      doc.setFontSize(7);
      doc.text('ALSO', margin, y);
      y += 3.5;
      tc(...C.body);
      doc.setFontSize(8);
      doc.text(sanitizeForPdf(analysis.secondary_categories.map(capitalize).join('  ·  ')), margin, y);
      y += 6;
    }

    // Creator's country (if available)
    const creatorCountry = singleNotes.creator_country as string | null | undefined;
    if (creatorCountry) {
      checkPage(8);
      tc(...C.muted);
      doc.setFontSize(7);
      doc.text('BASED IN', margin, y);
      y += 3.5;
      tc(...C.body);
      doc.setFontSize(8.5);
      doc.text(sanitizeForPdf(creatorCountry), margin, y);
      y += 6;
    }

    // Bio
    const bio = singleNotes.bio as string | null | undefined;
    if (bio?.trim()) {
      checkPage(16);
      tc(...C.muted);
      doc.setFontSize(7);
      doc.text('BIO', margin, y);
      y += 3.5;
      tc(...C.body);
      doc.setFontSize(8);
      const bioText = sanitizeForPdf(bio.trim());
      const bioLines = doc.splitTextToSize(bioText, contentW);
      for (const line of bioLines.slice(0, 4) as string[]) {
        checkPage(5);
        doc.text(line, margin, y);
        y += 4.5;
      }
      y += 2;
    }

    // Profile URL
    tc(...C.muted);
    doc.setFontSize(7);
    checkPage(6);
    doc.text(`Profile: ${creator.profile_url.slice(0, 60)}`, margin, y);
    y += 5;
  }

  // ════════════════════════════════════════════════════════════════
  // SECTION 2 — AUDIENCE QUALITY
  // ════════════════════════════════════════════════════════════════

  if (!multi && singleAnalysis) {
    const cred = singleAnalysis.credibility_score;
    const ppp  = singleAnalysis.paid_post_performance;

    if (cred != null || ppp != null) {
      sectionHeader('Audience Quality');

      if (cred != null) {
        checkPage(28);
        const realPct = Math.round(cred * 100);
        const fakePct = 100 - realPct;

        // Large credibility display
        const credColor: [number, number, number] =
          cred >= 0.80 ? C.green : cred >= 0.65 ? C.yellow : C.red;

        // Big number
        tc(...credColor);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(28);
        doc.text(`${realPct}%`, margin, y + 14);
        const bigW = doc.getTextWidth(`${realPct}%`);

        tc(...C.body);
        doc.setFontSize(10);
        doc.text('Real Followers', margin + bigW + 4, y + 10);

        tc(...C.muted);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8.5);
        doc.text(`${fakePct}% suspected fake / inactive`, margin + bigW + 4, y + 17);
        y += 20;

        // Full-width credibility bar
        checkPage(10);
        const barTotalW = contentW;
        fc(...credColor);
        doc.rect(margin, y, barTotalW * cred, 6, 'F');
        fc(...C.track);
        doc.rect(margin + barTotalW * cred, y, barTotalW * (1 - cred), 6, 'F');
        y += 9;

        // Benchmark note
        tc(...C.muted);
        doc.setFontSize(7.5);
        const benchmarkNote = cred >= 0.75
          ? `Above the 75% credibility benchmark. Audience appears authentic.`
          : `Below the 75% credibility benchmark. Recommend additional vetting.`;
        doc.text(benchmarkNote, margin, y);
        y += 7;
      }

      if (ppp != null) {
        checkPage(16);
        const pppPct = Math.round(ppp * 100);
        const pppColor: [number, number, number] =
          ppp >= 0.85 ? C.green : ppp >= 0.65 ? C.yellow : C.red;

        subLabel('Paid Post Performance (vs Organic)', 4);
        tc(...C.body);
        doc.setFontSize(8.5);
        doc.setFont('helvetica', 'bold');
        tc(...pppColor);
        doc.text(`${pppPct}%`, margin, y);
        tc(...C.muted);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.text('of organic engagement level on sponsored posts', margin + doc.getTextWidth(`${pppPct}%`) + 3, y);
        y += 5;

        const pppNote = ppp >= 0.85
          ? 'Audience responds well to sponsored content. Low ad fatigue.'
          : ppp >= 0.65
          ? 'Moderate drop in engagement on sponsored posts. Normal range.'
          : 'Significant drop in engagement on ads. Audience may be ad-fatigued.';
        tc(...C.muted);
        doc.setFontSize(7.5);
        doc.text(pppNote, margin, y);
        y += 7;
      }
    }
  }

  // ════════════════════════════════════════════════════════════════
  // SECTION 3 — FULL DEMOGRAPHICS
  // ════════════════════════════════════════════════════════════════

  sectionHeader('Audience Demographics');

  // Gender split
  const genderDist = mergedMetrics.gender_distribution
    ?? (singleAnalysis?.notes as Record<string, unknown> | null | undefined)?.gender_distribution as { male?: number; female?: number } | null;
  if (genderDist) {
    subLabel('Gender Split');
    const { male, female } = genderDist as { male?: number; female?: number };
    if (female != null) barRow(`Female`, female, [220, 100, 140]);
    if (male != null)   barRow(`Male`,   male,   [90, 120, 220]);
    y += 2;
  }

  // Age distribution — overall
  const ageRows = (mergedMetrics.age_distribution_overall ?? [])
    .filter(r => r.followers_pct != null)
    .slice(0, 8);
  if (ageRows.length > 0) {
    checkPage(8 * ageRows.length + 14);
    subLabel('Age Distribution (Overall)');
    for (const r of ageRows) barRow(r.label, r.followers_pct!, C.gold);
    y += 2;
  }

  // Age distribution — male / female split
  const ageMaleRows = ((mergedMetrics.age_distribution_male
    ?? (singleNotes.age_distribution_male as typeof mergedMetrics.age_distribution_male))
    ?? []).filter(r => r.followers_pct != null).slice(0, 7);
  const ageFemaleRows = ((mergedMetrics.age_distribution_female
    ?? (singleNotes.age_distribution_female as typeof mergedMetrics.age_distribution_male))
    ?? []).filter(r => r.followers_pct != null).slice(0, 7);

  if (ageMaleRows.length > 0 || ageFemaleRows.length > 0) {
    checkPage(8 * Math.max(ageMaleRows.length, ageFemaleRows.length) + 18);
    subLabel('Age by Gender');

    const halfW = (contentW - 6) / 2;
    const savedY = y;
    let leftY = y;
    let rightY = y;

    // Male column
    if (ageMaleRows.length > 0) {
      tc(...C.muted);
      doc.setFontSize(7);
      doc.setFont('helvetica', 'bold');
      doc.text('MALE', margin, leftY);
      doc.setFont('helvetica', 'normal');
      leftY += 4;
      for (const r of ageMaleRows) {
        const barX = margin + 28;
        const barW = halfW - 30;
        tc(...C.body);
        doc.setFontSize(7.5);
        doc.text(r.label, margin, leftY + 3);
        fc(...C.track);
        doc.rect(barX, leftY + 1, barW, 3, 'F');
        fc(...([90, 120, 220] as [number, number, number]));
        doc.rect(barX, leftY + 1, barW * Math.min(r.followers_pct! * 2, 1), 3, 'F');
        tc(...C.muted);
        doc.setFontSize(6.5);
        doc.text(`${(r.followers_pct! * 100).toFixed(1)}%`, barX + barW + 1, leftY + 3.5);
        leftY += 7;
      }
    }

    y = savedY;
    rightY = savedY;

    // Female column
    if (ageFemaleRows.length > 0) {
      const rightMargin = margin + halfW + 6;
      tc(...C.muted);
      doc.setFontSize(7);
      doc.setFont('helvetica', 'bold');
      doc.text('FEMALE', rightMargin, rightY);
      doc.setFont('helvetica', 'normal');
      rightY += 4;
      for (const r of ageFemaleRows) {
        const barX = rightMargin + 28;
        const barW = halfW - 30;
        tc(...C.body);
        doc.setFontSize(7.5);
        doc.text(r.label, rightMargin, rightY + 3);
        fc(...C.track);
        doc.rect(barX, rightY + 1, barW, 3, 'F');
        fc(...([220, 100, 140] as [number, number, number]));
        doc.rect(barX, rightY + 1, barW * Math.min(r.followers_pct! * 2, 1), 3, 'F');
        tc(...C.muted);
        doc.setFontSize(6.5);
        doc.text(`${(r.followers_pct! * 100).toFixed(1)}%`, barX + barW + 1, rightY + 3.5);
        rightY += 7;
      }
    }

    y = Math.max(leftY, rightY) + 3;
  }

  // Country geo
  const fallbackGeo = multi
    ? result.platforms[0]?.analysis.geo_top_countries
    : singleAnalysis?.geo_top_countries;
  const geoRows = (
    mergedMetrics.geo_distribution?.filter(r => r.followers_pct != null)
    ?? fallbackGeo?.map(g => ({ label: g.country, followers_pct: g.pct }))
    ?? []
  ).slice(0, 7);
  if (geoRows.length > 0) {
    checkPage(8 * geoRows.length + 14);
    subLabel('Top Countries');
    for (const r of geoRows) barRow(r.label, r.followers_pct ?? 0, C.teal);
    y += 2;
  }

  // Audience cities
  const cities = singleAnalysis?.audience_cities ?? null;
  if (cities && cities.length > 0) {
    checkPage(8 * Math.min(cities.length, 5) + 14);
    subLabel('Top Cities');
    for (const c of cities.slice(0, 5)) {
      barRow(c.name ?? c.code ?? '?', c.weight, [60, 180, 100]);
    }
    y += 2;
  }

  // Audience languages
  const langs = singleAnalysis?.audience_languages ?? null;
  if (langs && langs.length > 0) {
    checkPage(8 * Math.min(langs.length, 5) + 14);
    subLabel('Audience Languages');
    for (const l of langs.slice(0, 5)) {
      barRow(l.name ?? l.code, l.weight, C.blue);
    }
    y += 2;
  }

  // Audience ethnicities (Instagram only)
  const ethnicities = singleAnalysis?.audience_ethnicities ?? null;
  if (ethnicities && ethnicities.length > 0) {
    checkPage(8 * Math.min(ethnicities.length, 5) + 14);
    subLabel('Audience Ethnicity');
    for (const e of ethnicities.slice(0, 5)) {
      barRow(e.name ?? e.code, e.weight, C.purple);
    }
    y += 2;
  }

  // Audience interests
  const interests = (mergedMetrics.interests_ranked ?? []).filter(r => r.followers_pct != null).slice(0, 7);
  if (interests.length > 0) {
    checkPage(8 * interests.length + 14);
    subLabel('Audience Interests');
    for (const r of interests) barRow(r.label, r.followers_pct ?? 0, C.purple);
    y += 2;
  }

  // Audience brand affinity
  const brands = (mergedMetrics.brand_affinity_ranked ?? []).filter(r => r.followers_pct != null).slice(0, 6);
  if (brands.length > 0) {
    checkPage(8 * brands.length + 14);
    subLabel('Audience Brand Affinity');
    for (const r of brands) barRow(r.label, r.followers_pct ?? 0, C.gold);
    y += 2;
  }

  // ════════════════════════════════════════════════════════════════
  // SECTION 4 — CREATOR SIGNALS
  // ════════════════════════════════════════════════════════════════

  const creatorInterests  = singleNotes.creator_interests as string[] | null | undefined;
  const creatorBrandAff   = singleNotes.creator_brand_affinity as string[] | null | undefined;
  const popularHashtags   = singleNotes.popular_hashtags as Array<{ tag: string; pct: number }> | null | undefined;
  const popularMentions   = singleNotes.popular_mentions as Array<{ mention: string; pct: number }> | null | undefined;

  const hasCreatorSignals = (creatorInterests?.length ?? 0) > 0
    || (creatorBrandAff?.length ?? 0) > 0
    || (popularHashtags?.length ?? 0) > 0
    || (popularMentions?.length ?? 0) > 0;

  if (!multi && hasCreatorSignals) {
    sectionHeader('Creator Signals');

    if (creatorInterests && creatorInterests.length > 0) {
      subLabel("Creator's Own Interests");
      // Render as chips
      checkPage(10);
      let chipX = margin;
      let chipY = y;
      for (const interest of creatorInterests.slice(0, 12)) {
        const label = sanitizeForPdf(interest);
        const chipW = doc.getTextWidth(label) + 6;
        if (chipX + chipW > margin + contentW) {
          chipX = margin;
          chipY += 7;
          checkPage(7);
        }
        fc(...C.gold);
        doc.roundedRect(chipX, chipY - 3, chipW, 5.5, 1, 1, 'F');
        tc(...C.black);
        doc.setFontSize(7);
        doc.setFont('helvetica', 'bold');
        doc.text(label, chipX + 3, chipY + 0.5);
        doc.setFont('helvetica', 'normal');
        chipX += chipW + 3;
      }
      y = chipY + 9;
    }

    if (creatorBrandAff && creatorBrandAff.length > 0) {
      subLabel("Creator's Brand Affinities");
      checkPage(10);
      let chipX = margin;
      let chipY = y;
      for (const brand of creatorBrandAff.slice(0, 12)) {
        const label = sanitizeForPdf(brand);
        const chipW = doc.getTextWidth(label) + 6;
        if (chipX + chipW > margin + contentW) {
          chipX = margin;
          chipY += 7;
          checkPage(7);
        }
        fc(...C.cardBg);
        dc(...C.divider);
        doc.setLineWidth(0.3);
        doc.roundedRect(chipX, chipY - 3, chipW, 5.5, 1, 1, 'FD');
        tc(...C.body);
        doc.setFontSize(7);
        doc.setFont('helvetica', 'bold');
        doc.text(label, chipX + 3, chipY + 0.5);
        doc.setFont('helvetica', 'normal');
        chipX += chipW + 3;
      }
      y = chipY + 9;
    }

    if (popularHashtags && popularHashtags.length > 0) {
      checkPage(8 * Math.min(popularHashtags.length, 8) + 14);
      subLabel('Popular Hashtags');
      for (const h of popularHashtags.slice(0, 8)) {
        barRow(`#${h.tag}`, h.pct, C.teal, 70);
      }
      y += 2;
    }

    if (popularMentions && popularMentions.length > 0) {
      checkPage(8 * Math.min(popularMentions.length, 6) + 14);
      subLabel('Popular Mentions');
      for (const m of popularMentions.slice(0, 6)) {
        barRow(`@${m.mention}`, m.pct, C.blue, 70);
      }
      y += 2;
    }
  }

  // ════════════════════════════════════════════════════════════════
  // SECTION 5 — BRAND COLLABORATIONS
  // ════════════════════════════════════════════════════════════════

  if (!multi && collabBrands.length > 0) {
    sectionHeader('Brand Collaborations');

    // Summary line
    checkPage(8);
    tc(...C.muted);
    doc.setFontSize(8);
    doc.text(
      `${collabBrands.length} brand${collabBrands.length !== 1 ? 's' : ''} identified from collaboration posts`,
      margin, y,
    );
    y += 7;

    for (const brand of collabBrands) {
      checkPage(18);

      // Brand name row
      tc(...C.black);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8.5);
      doc.text(sanitizeForPdf(brand.name), margin, y);

      // Collab type pill
      const typeColor: [number, number, number] =
        brand.collaboration_type === 'Ambassador' ? C.gold
        : brand.collaboration_type === 'Paid'     ? C.green
        : brand.collaboration_type === 'Affiliate'? C.blue
        : brand.collaboration_type === 'Gifted'   ? C.purple
        : C.subtle;
      const typeTextColor: [number, number, number] =
        brand.collaboration_type === 'Ambassador' ? C.black : C.white;
      const nameW = doc.getTextWidth(sanitizeForPdf(brand.name)) + 4;
      pill(brand.collaboration_type, margin + nameW, y, typeColor, typeTextColor);

      y += 5;

      // Category + post count
      tc(...C.muted);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(7.5);
      const metaParts = [brand.category];
      metaParts.push(`${brand.post_count} post${brand.post_count !== 1 ? 's' : ''}`);
      if (brand.total_likes)    metaParts.push(`${fmtNum(brand.total_likes)} likes`);
      if (brand.total_comments) metaParts.push(`${fmtNum(brand.total_comments)} comments`);
      if (brand.total_views)    metaParts.push(`${fmtNum(brand.total_views)} views`);
      doc.text(sanitizeForPdf(metaParts.join('  ·  ')), margin, y);
      y += 4;

      if (brand.domain) {
        tc(...C.subtle);
        doc.setFontSize(7);
        doc.text(brand.domain, margin, y);
        y += 4;
      }

      divider(3);
    }
    y += 2;
  }

  // ════════════════════════════════════════════════════════════════
  // SECTION 6 — TOP PRODUCT RECOMMENDATIONS
  // ════════════════════════════════════════════════════════════════

  sectionHeader('Top 5 Product Recommendations');

  const productImages = await Promise.all(
    result.top_products.map(p =>
      p.primary_image_url ? loadImgBase64(p.primary_image_url) : Promise.resolve(null),
    ),
  );

  for (let pi = 0; pi < result.top_products.length; pi++) {
    const p = result.top_products[pi];
    const imgData = productImages[pi];
    const rowH = imgData ? 22 : 14;
    checkPage(rowH + 28);

    fc(...C.cardBg);
    doc.rect(margin, y, contentW, rowH, 'F');
    fc(...C.gold);
    doc.rect(margin, y, 8, rowH, 'F');
    tc(...C.black);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text(String(p.rank), margin + 4, y + rowH / 2 + 1.5, { align: 'center' });

    let titleX = margin + 11;
    if (imgData) {
      try { doc.addImage(imgData, 'JPEG', margin + 10, y + 3, 16, 16); } catch { /* skip */ }
      titleX = margin + 29;
    }

    tc(...C.black);
    doc.setFontSize(8.5);
    doc.setFont('helvetica', 'bold');
    doc.text(sanitizeForPdf(p.title.slice(0, 65)), titleX, y + (imgData ? 9 : 6), {
      maxWidth: contentW - (titleX - margin) - 16,
    });
    tc(...C.muted);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.text(
      sanitizeForPdf(`${p.vertical} / ${p.category}  -  $${p.price_min}-$${p.price_max}  -  ${p.tags.slice(0, 3).join(', ')}`),
      titleX, y + (imgData ? 15 : 11.5),
    );

    const scorePct = Math.round(p.score * 100);
    tc(...C.gold);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text(`${scorePct}%`, W - margin, y + rowH / 2 + 1.5, { align: 'right' });
    y += rowH + 2;

    if (p.gmv_expected != null) {
      checkPage(8);
      tc(...C.muted);
      doc.setFontSize(7);
      doc.setFont('helvetica', 'normal');
      doc.text('Est. GMV:', margin + 11, y + 3.5);
      tc(...C.body); doc.setFont('helvetica', 'bold');
      doc.text(fmtMoney(p.gmv_low ?? 0), margin + 35, y + 3.5);
      tc(...C.muted); doc.setFont('helvetica', 'normal');
      doc.text('–', margin + 60, y + 3.5);
      tc(...C.gold); doc.setFont('helvetica', 'bold');
      doc.text(fmtMoney(p.gmv_expected), margin + 65, y + 3.5);
      tc(...C.muted); doc.setFont('helvetica', 'normal');
      doc.text('–', margin + 90, y + 3.5);
      tc(...C.body); doc.setFont('helvetica', 'bold');
      doc.text(fmtMoney(p.gmv_high ?? 0), margin + 95, y + 3.5);
      doc.setFont('helvetica', 'normal');
      y += 7;
    }

    // Mini factor bar chart
    const factors: [string, number][] = [
      ['Category', p.factor_breakdown.category.raw_score],
      ['Tags',     p.factor_breakdown.tags.raw_score],
      ['Age',      p.factor_breakdown.age.raw_score],
      ['Price',    p.factor_breakdown.price.raw_score],
      ['Margin',   p.factor_breakdown.margin.raw_score],
    ];
    const fW = (contentW - 4) / factors.length;
    for (let fi = 0; fi < factors.length; fi++) {
      const [fname, fval] = factors[fi];
      const fx = margin + fi * (fW + 1);
      const barH = Math.max(1, fval * 10);
      const baseY = y + 12;
      fc(...C.track); doc.rect(fx, baseY - 10, fW, 10, 'F');
      fc(...C.gold);  doc.rect(fx, baseY - barH, fW, barH, 'F');
      tc(...C.muted);
      doc.setFontSize(6.5);
      doc.setFont('helvetica', 'normal');
      doc.text(fname, fx + fW / 2, baseY + 3.5, { align: 'center' });
    }
    y += 19;

    const topFactor = Object.entries(p.factor_breakdown)
      .filter(([k]) => k !== 'total_score')
      .map(([, v]) => v as FactorEntry)
      .sort((a, b) => b.contribution - a.contribution)[0];
    if (topFactor?.why) {
      checkPage(10);
      tc(...C.body);
      doc.setFontSize(8);
      const lines = doc.splitTextToSize(sanitizeForPdf(`Why it fits: ${topFactor.why}`), contentW);
      doc.text(lines.slice(0, 3) as string[], margin, y);
      y += (lines.slice(0, 3).length * 4.5);
    }
    y += 4;
    divider();
  }

  // ════════════════════════════════════════════════════════════════
  // SECTION 7 — GMV ESTIMATE
  // ════════════════════════════════════════════════════════════════

  sectionHeader('Revenue Potential (GMV Estimate)');

  if (multi && result.gmv.platform_breakdown.length > 1) {
    checkPage(12 + result.gmv.platform_breakdown.length * 10);
    subLabel('Platform GMV Breakdown');
    for (const pb of result.gmv.platform_breakdown) {
      checkPage(10);
      tc(...C.body); doc.setFont('helvetica', 'bold'); doc.setFontSize(8.5);
      doc.text(`${capitalize(pb.platform)}  @${pb.handle}`, margin, y + 3.5);
      tc(...C.muted); doc.setFont('helvetica', 'normal'); doc.setFontSize(7.5);
      doc.text(`${fmtNum(pb.followers)} followers  ·  ${fmtPct(pb.engagement_rate)}`, margin + 60, y + 3.5);
      tc(...C.gold); doc.setFont('helvetica', 'bold');
      doc.text(fmtMoney(pb.gmv_expected), W - margin, y + 3.5, { align: 'right' });
      doc.setFont('helvetica', 'normal');
      y += 8;
    }
    tc(...C.muted); doc.setFontSize(7.5);
    doc.text(`~${Math.round((1 - result.gmv.overlap_factor) * 100)}% audience overlap adjustment applied`, margin, y + 3);
    y += 8;
    divider();
  }

  // Tier card
  checkPage(28);
  fc(...C.black); doc.rect(margin, y, contentW, 26, 'F');
  fc(...C.gold);  doc.rect(margin, y, 3, 26, 'F');
  tc(...C.gold); doc.setFont('helvetica', 'bold'); doc.setFontSize(11);
  doc.text(TIER_LABELS[gmvTierVal] ?? 'Unknown Tier', margin + 7, y + 9);
  tc(200, 200, 200); doc.setFont('helvetica', 'normal'); doc.setFontSize(8.5);
  const tierSubtext = multi
    ? `Confidence: ${gmvConfidence}%  ·  Combined across ${result.gmv.platform_breakdown.length} platforms`
    : `Confidence: ${gmvConfidence}%  ·  AOV: ${fmtMoney(gmvAov)}`;
  doc.text(tierSubtext, margin + 7, y + 18);
  y += 30;

  // GMV bands
  checkPage(22);
  const bw = (contentW - 8) / 3;
  const boxes: [string, string, boolean][] = [
    ['Conservative', fmtMoney(gmvLow), false],
    ['Expected',     fmtMoney(gmvExpected), true],
    ['Optimistic',   fmtMoney(gmvHigh), false],
  ];
  boxes.forEach(([label, val, highlight], i) => {
    const bx = margin + i * (bw + 4);
    if (highlight) {
      fc(...C.gold); doc.rect(bx, y, bw, 18, 'F'); tc(...C.black);
    } else {
      fc(...C.cardBg); doc.rect(bx, y, bw, 18, 'F');
      dc(...C.divider); doc.setLineWidth(0.25); doc.rect(bx, y, bw, 18, 'S');
      tc(...C.muted);
    }
    doc.setFont('helvetica', 'normal'); doc.setFontSize(7.5);
    doc.text(label.toUpperCase(), bx + bw / 2, y + 5.5, { align: 'center' });
    doc.setFont('helvetica', 'bold'); doc.setFontSize(11);
    if (!highlight) tc(...C.black);
    doc.text(val, bx + bw / 2, y + 13.5, { align: 'center' });
  });
  y += 22;

  // ════════════════════════════════════════════════════════════════
  // NEXT STEPS
  // ════════════════════════════════════════════════════════════════

  checkPage(36);
  sectionHeader('Tier Classification & Next Steps');

  checkPage(20);
  tc(...C.black); doc.setFont('helvetica', 'bold'); doc.setFontSize(9.5);
  doc.text(`Classification: ${TIER_LABELS[gmvTierVal] ?? '—'}`, margin, y);
  y += 6;
  tc(...C.body); doc.setFont('helvetica', 'normal'); doc.setFontSize(8.5);
  const actionLines = doc.splitTextToSize(TIER_ACTIONS[gmvTierVal] ?? '', contentW);
  doc.text(actionLines as string[], margin, y);
  y += actionLines.length * 5 + 5;

  checkPage(12);
  tc(...C.muted); doc.setFontSize(7.5);
  const disclaimer = 'GMV estimates are projections based on historical benchmarks and are not guaranteed. '
    + 'Conversion rates vary by content type, product niche, and audience engagement patterns.';
  const dLines = doc.splitTextToSize(disclaimer, contentW);
  doc.text(dLines as string[], margin, y);

  // ── Apply footer to every page ───────────────────────────────────────────
  const totalPages = doc.getNumberOfPages();
  for (let pg = 1; pg <= totalPages; pg++) {
    doc.setPage(pg);
    pageFooter();
  }

  const handleForFilename = multi
    ? result.platforms.map(p => p.handle).join('-')
    : singleResult!.creator.handle;
  doc.save(`creatorhub360-report-${handleForFilename}-${Date.now()}.pdf`);
}
